/**
 * AudioManager - Singleton para reproducción de audio
 *
 * Responsabilidades:
 * - Reproducir música con Howler.js (desde cache o URL)
 * - Reproducir SFX desde cache precargado
 * - Event delegation para data-sfx (opt-in clicks/hover sounds)
 * - Suscribirse a useConfigStore para volumen/mute
 * - Manejar fade in/out
 * - Respetar browser audio policy
 */

import { Howl, Howler } from 'howler';
import { Asset } from '@rts';
import { Storage } from '@storage';
import { getAssetPath } from '@utils/brandHelper';
import useConfigStore from '@/store/useConfigStore';

class AudioManagerClass {
  constructor() {
    this.currentMusic = null;
    this.currentMusicKey = null;
    this.isUnlocked = false;
    this.pendingPlay = null; // Key to play after unlock
    this.unsubscribe = null;

    // Playlist state
    this.playlist = [];           // Shuffled array of track keys
    this.playlistIndex = 0;       // Current index in playlist
    this.playlistCategory = null; // Current category (e.g., 'battle')
    this.isPlaylistMode = false;  // True when playing a playlist

    // Event delegation handlers
    this._clickHandler = null;
    this._mouseEnterHandler = null;
    this._sfxDelegationInitialized = false;

    // Fade durations in ms
    this.FADE_IN_DURATION = 2000;
    this.FADE_OUT_DURATION = 1000;
  }

  /**
   * Inicializa el AudioManager y suscribe a cambios de settings
   */
  init() {
    if (this.unsubscribe) return; // Already initialized

    // Track previous values for comparison
    let prevSoundEnabled = useConfigStore.getState().settings.sound_enabled;
    let prevMusicVolume = useConfigStore.getState().settings.music_volume;
    let prevSfxVolume = useConfigStore.getState().settings.sfx_volume;

    // Subscribe to config changes (Zustand default subscribe)
    this.unsubscribe = useConfigStore.subscribe((state) => {
      const soundEnabled = state.settings.sound_enabled;
      const musicVolume = state.settings.music_volume;
      const sfxVolume = state.settings.sfx_volume;

      // Only react if values actually changed
      if (
        soundEnabled !== prevSoundEnabled ||
        musicVolume !== prevMusicVolume ||
        sfxVolume !== prevSfxVolume
      ) {
        const previous = {
          sound_enabled: prevSoundEnabled,
          music_volume: prevMusicVolume,
          sfx_volume: prevSfxVolume,
        };
        const current = {
          sound_enabled: soundEnabled,
          music_volume: musicVolume,
          sfx_volume: sfxVolume,
        };

        prevSoundEnabled = soundEnabled;
        prevMusicVolume = musicVolume;
        prevSfxVolume = sfxVolume;

        this._onSettingsChange(current, previous);
      }
    });

    // Apply initial settings
    this._applyVolume();

    // Initialize SFX event delegation
    this.initSfxEventDelegation();

    console.debug('[AudioManager] Initialized');
  }

  /**
   * Desbloquea el audio context (llamar en primer click del usuario)
   */
  unlock() {
    console.log(`[AudioManager] unlock() called, isUnlocked: ${this.isUnlocked}`);
    if (this.isUnlocked) return;
    this.isUnlocked = true;
    console.log('[AudioManager] Audio unlocked!');

    // Resume suspended audio context
    console.log(`[AudioManager] Howler.ctx.state before resume: ${Howler.ctx?.state}`);
    if (Howler.ctx && Howler.ctx.state === 'suspended') {
      Howler.ctx.resume().then(() => {
        console.log('[AudioManager] AudioContext resumed successfully');
        this._tryPlayPending();
      });
    } else {
      this._tryPlayPending();
    }
  }

  /**
   * Intenta reproducir música pendiente o reanudar la actual
   * @private
   */
  _tryPlayPending() {
    console.log(`[AudioManager] _tryPlayPending called, pendingPlay: ${this.pendingPlay}, currentMusicKey: ${this.currentMusicKey}`);

    // If we have pending music key, play it
    if (this.pendingPlay) {
      const key = this.pendingPlay;
      this.pendingPlay = null;
      console.log(`[AudioManager] Playing pending music: ${key}`);
      this._forcePlay(key);
      return;
    }

    // If we have current music that's not playing, restart it
    if (this.currentMusicKey && this.currentMusic && !this.currentMusic.playing()) {
      console.log('[AudioManager] Resuming paused music');
      this.currentMusic.play();
    }
  }

  /**
   * Fuerza la reproducción de música (después de unlock)
   * Intenta usar audio precargado de StorageManager primero
   * @private
   */
  _forcePlay(key) {
    console.log(`[AudioManager] _forcePlay called with: ${key}`);

    // Stop any existing music first
    if (this.currentMusic) {
      console.log('[AudioManager] Stopping existing music');
      this.currentMusic.unload();
      this.currentMusic = null;
    }

    const settings = useConfigStore.getState().settings;
    if (!settings.sound_enabled) {
      console.log('[AudioManager] Sound disabled in _forcePlay, returning');
      return;
    }

    const volume = this._calculateMusicVolume();
    console.log(`[AudioManager] Calculated volume: ${volume}`);

    // Try to get precached Howl from Storage
    const cachedHowl = Storage.getPreloadedAudio('music', key);
    console.log(`[AudioManager] Cached Howl for ${key}: ${cachedHowl ? 'FOUND' : 'NOT FOUND'}`);

    if (cachedHowl) {
      // Clone the cached Howl for playback (allows independent control)
      // Actually, Howl instances can't be cloned, so we use the cached one directly
      // But we need to configure it for music playback
      this.currentMusic = cachedHowl;
      this.currentMusic.loop(true);
      this.currentMusicKey = key;

      // Fade in
      this.currentMusic.volume(0);
      this.currentMusic.once('play', () => {
        console.debug(`[AudioManager] Playing from cache: ${key}`);
        if (this.currentMusic) {
          this.currentMusic.fade(0, volume, this.FADE_IN_DURATION);
        }
      });

      this.currentMusic.play();
      return;
    }

    // Fallback: load from URL if not precached
    const url = this._getMusicUrl(key);
    console.log(`[AudioManager] Fallback URL for ${key}: ${url}`);
    if (!url) {
      console.warn(`[AudioManager] Could not resolve URL for music: ${key}`);
      return;
    }

    console.log(`[AudioManager] Creating new Howl from URL: ${url}`);
    this.currentMusic = new Howl({
      src: [url],
      loop: true,
      volume: volume, // Start at target volume (no fade on loops)
      html5: true,
      onloaderror: (id, error) => {
        console.error(`[AudioManager] Load error for ${key}:`, error);
      },
    });

    this.currentMusicKey = key;

    // Fade in only on first play (not on loops)
    this.currentMusic.volume(0);
    this.currentMusic.once('play', () => {
      console.debug(`[AudioManager] Playing: ${key}`);
      if (this.currentMusic) {
        this.currentMusic.fade(0, volume, this.FADE_IN_DURATION);
      }
    });

    this.currentMusic.play();
  }

  /**
   * Reproduce música con fade in
   * @param {string} key - Key del asset (ej: 'maintheme')
   */
  playMusic(key) {
    console.log(`[AudioManager] playMusic called with: ${key}`);

    // If same music already playing, do nothing
    if (this.currentMusicKey === key && this.currentMusic && this.currentMusic.playing()) {
      console.log(`[AudioManager] Same music already playing, skipping`);
      return;
    }

    // If same music already pending (waiting for unlock), do nothing
    if (this.pendingPlay === key) {
      console.log(`[AudioManager] Same music already pending, skipping`);
      return;
    }

    // Check if sound is enabled
    const settings = useConfigStore.getState().settings;
    console.log(`[AudioManager] sound_enabled: ${settings.sound_enabled}, music_volume: ${settings.music_volume}`);
    if (!settings.sound_enabled) {
      console.log('[AudioManager] Sound disabled, not playing');
      return;
    }

    // If audio context is suspended (no user interaction yet), store as pending
    console.log(`[AudioManager] Howler.ctx state: ${Howler.ctx?.state}`);
    if (Howler.ctx && Howler.ctx.state === 'suspended') {
      console.log(`[AudioManager] AudioContext suspended, queuing: ${key}`);
      this.pendingPlay = key;
      this.currentMusicKey = key; // Track what we want to play
      return;
    }

    // Audio context is ready, use force play
    console.log(`[AudioManager] Calling _forcePlay for: ${key}`);
    this._forcePlay(key);
  }

  /**
   * Para la música con fade out (mantiene el key para poder reanudar)
   */
  stopMusic() {
    if (!this.currentMusic) return;
    this._stopCurrentMusic();
    // Keep currentMusicKey so we can resume later
  }

  /**
   * Verifica si hay música reproduciéndose
   * @returns {boolean}
   */
  isPlaying() {
    return this.currentMusic && this.currentMusic.playing();
  }

  /**
   * Obtiene la URL del archivo de música
   * @private
   */
  _getMusicUrl(key) {
    // Try Asset system first (post-login or if loaded)
    if (Asset.isLoaded()) {
      try {
        const asset = Asset.buildUrl('music', key);
        return asset.value;
      } catch (error) {
        console.warn('[AudioManager] Asset.buildUrl failed:', error.message);
      }
    }

    // Fallback: use brand system directly (pre-login)
    return getAssetPath(`theme/base/music/${key}.mp3`);
  }

  /**
   * Calcula el volumen de música basado en settings
   * @private
   */
  _calculateMusicVolume() {
    const settings = useConfigStore.getState().settings;
    if (!settings.sound_enabled) return 0;

    // music_volume is 0-100, Howler expects 0-1
    return settings.music_volume / 100;
  }

  /**
   * Calcula el volumen de SFX basado en settings
   * @private
   */
  _calculateSfxVolume() {
    const settings = useConfigStore.getState().settings;
    if (!settings.sound_enabled) return 0;

    // sfx_volume is 0-100, Howler expects 0-1
    return settings.sfx_volume / 100;
  }

  /**
   * Aplica el volumen actual a la música
   * @private
   */
  _applyVolume() {
    if (!this.currentMusic) return;

    const volume = this._calculateMusicVolume();
    this.currentMusic.volume(volume);
  }

  /**
   * Callback cuando cambian los settings
   * @private
   */
  _onSettingsChange(current, previous) {
    // If sound was disabled, stop music
    if (!current.sound_enabled && previous?.sound_enabled) {
      this.stopMusic();
      return;
    }

    // If sound was enabled (either toggled on, or volume raised from 0)
    const wasDisabled = !previous?.sound_enabled || previous?.music_volume === 0;
    const isNowEnabled = current.sound_enabled && current.music_volume > 0;

    if (isNowEnabled && wasDisabled && this.currentMusicKey) {
      // Need to restart music - currentMusic might be null after stop
      if (!this.currentMusic || !this.currentMusic.playing()) {
        console.debug('[AudioManager] Resuming music:', this.currentMusicKey);
        this._forcePlay(this.currentMusicKey);
      }
      return;
    }

    // Just volume change on existing music
    this._applyVolume();
  }

  /**
   * Para la música actual con fade out
   * @private
   */
  _stopCurrentMusic() {
    if (!this.currentMusic) return;

    const music = this.currentMusic;
    const currentVolume = music.volume();

    // Fade out then unload
    music.fade(currentVolume, 0, this.FADE_OUT_DURATION);
    music.once('fade', () => {
      music.unload();
    });

    this.currentMusic = null;
  }

  // ==================== PLAYLIST ====================

  /**
   * Fisher-Yates shuffle algorithm
   * @param {Array} array - Array to shuffle (mutates in place)
   * @returns {Array} - The shuffled array
   * @private
   */
  _shuffleArray(array) {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  }

  /**
   * Reproduce una playlist de música con shuffle
   * @param {string} category - Categoría de la playlist (ej: 'battle')
   * @param {Array} tracks - Array de track keys (ej: ['battle/track1', 'battle/track2'])
   */
  playPlaylist(category, tracks) {
    if (!tracks || tracks.length === 0) {
      console.warn(`[AudioManager] No tracks provided for playlist: ${category}`);
      return;
    }

    // Stop any current music
    this._stopCurrentMusic();

    // Setup playlist
    this.playlistCategory = category;
    this.playlist = this._shuffleArray([...tracks]); // Clone and shuffle
    this.playlistIndex = 0;
    this.isPlaylistMode = true;

    console.debug(`[AudioManager] Starting playlist: ${category} (${this.playlist.length} tracks)`);

    // Start first track
    this._playCurrentPlaylistTrack();
  }

  /**
   * Reproduce el track actual de la playlist
   * @private
   */
  _playCurrentPlaylistTrack() {
    if (!this.isPlaylistMode || this.playlist.length === 0) return;

    const key = this.playlist[this.playlistIndex];

    // Check if sound is enabled
    const settings = useConfigStore.getState().settings;
    if (!settings.sound_enabled) {
      console.debug('[AudioManager] Sound disabled, not playing playlist');
      return;
    }

    // If audio context is suspended, store as pending
    if (Howler.ctx && Howler.ctx.state === 'suspended') {
      console.debug(`[AudioManager] AudioContext suspended, queuing playlist track: ${key}`);
      this.pendingPlay = key;
      this.currentMusicKey = key;
      return;
    }

    this._forcePlayPlaylistTrack(key);
  }

  /**
   * Fuerza la reproducción de un track de playlist (sin loop, con onend)
   * @private
   */
  _forcePlayPlaylistTrack(key) {
    // Stop any existing music first
    if (this.currentMusic) {
      this.currentMusic.unload();
      this.currentMusic = null;
    }

    const settings = useConfigStore.getState().settings;
    if (!settings.sound_enabled) return;

    const volume = this._calculateMusicVolume();

    // Try to get precached Howl from Storage
    const cachedHowl = Storage.getPreloadedAudio('music', key);

    if (cachedHowl) {
      this.currentMusic = cachedHowl;
      this.currentMusic.loop(false); // No loop for playlist
      this.currentMusicKey = key;

      // Setup onend for next track
      this.currentMusic.off('end'); // Remove previous handlers
      this.currentMusic.on('end', () => this._onPlaylistTrackEnd());

      // Fade in
      this.currentMusic.volume(0);
      this.currentMusic.once('play', () => {
        console.debug(`[AudioManager] Playing playlist track from cache: ${key}`);
        if (this.currentMusic) {
          this.currentMusic.fade(0, volume, this.FADE_IN_DURATION);
        }
      });

      this.currentMusic.play();
      return;
    }

    // Fallback: load from URL if not precached
    const url = this._getMusicUrl(key);
    if (!url) {
      console.warn(`[AudioManager] Could not resolve URL for playlist track: ${key}`);
      this._onPlaylistTrackEnd(); // Skip to next
      return;
    }

    this.currentMusic = new Howl({
      src: [url],
      loop: false,
      volume: 0,
      html5: true,
      onend: () => this._onPlaylistTrackEnd(),
      onloaderror: (id, error) => {
        console.error(`[AudioManager] Load error for ${key}:`, error);
        this._onPlaylistTrackEnd(); // Skip to next on error
      },
    });

    this.currentMusicKey = key;

    this.currentMusic.once('play', () => {
      console.debug(`[AudioManager] Playing playlist track: ${key}`);
      if (this.currentMusic) {
        this.currentMusic.fade(0, volume, this.FADE_IN_DURATION);
      }
    });

    this.currentMusic.play();
  }

  /**
   * Callback cuando termina un track de la playlist
   * @private
   */
  _onPlaylistTrackEnd() {
    if (!this.isPlaylistMode) return;

    // Move to next track
    this.playlistIndex++;

    // If we've played all tracks, reshuffle and start over
    if (this.playlistIndex >= this.playlist.length) {
      console.debug('[AudioManager] Playlist complete, reshuffling');
      this._shuffleArray(this.playlist);
      this.playlistIndex = 0;
    }

    // Play next track
    this._playCurrentPlaylistTrack();
  }

  /**
   * Para la playlist actual
   */
  stopPlaylist() {
    this.isPlaylistMode = false;
    this.playlist = [];
    this.playlistIndex = 0;
    this.playlistCategory = null;
    this._stopCurrentMusic();
  }

  /**
   * Reproduce música solo si no hay nada sonando
   * @param {string} key - Key del asset (ej: 'maintheme')
   */
  playMusicIfSilent(key) {
    console.log(`[AudioManager] playMusicIfSilent called with: ${key}`);
    console.log(`[AudioManager] isPlaying: ${this.isPlaying()}, isPlaylistMode: ${this.isPlaylistMode}`);

    if (this.isPlaying()) {
      console.log(`[AudioManager] Music already playing, skipping: ${key}`);
      return;
    }
    console.log(`[AudioManager] No music playing, calling playMusic: ${key}`);
    this.playMusic(key);
  }

  // ==================== SFX ====================

  /**
   * Reproduce un efecto de sonido (SFX)
   * Usa audio precargado de StorageManager
   * @param {string} key - Key del SFX (ej: 'button', 'input')
   */
  playSfx(key) {
    const settings = useConfigStore.getState().settings;
    if (!settings.sound_enabled) return;

    const volume = this._calculateSfxVolume();
    if (volume === 0) return;

    // Get precached Howl from Storage
    const howl = Storage.getPreloadedAudio('sounds', key);

    if (!howl) {
      // SFX not precached - this is expected for non-critical sounds
      // Don't spam console, just skip
      return;
    }

    // Play at current volume
    howl.volume(volume);
    howl.play();
  }

  /**
   * Inicializa event delegation para data-sfx attributes
   * Permite sonidos opt-in en elementos UI
   *
   * Uso:
   * - <button data-sfx="button">Click me</button> → suena al click
   * - <input data-sfx-hover="input" /> → suena al hover
   */
  initSfxEventDelegation() {
    if (this._sfxDelegationInitialized) return;

    // Click handler para data-sfx
    this._clickHandler = (e) => {
      const el = e.target.closest('[data-sfx]');
      if (el) {
        const sfxKey = el.dataset.sfx;
        const howl = Storage.getPreloadedAudio('sounds', sfxKey);
        const status = howl ? 'loaded' : 'not loaded';
        console.debug(`[AudioManager] SFX click: "${sfxKey}" (${status})`);
        this.playSfx(sfxKey);
      }
    };

    // Mouseenter handler para data-sfx-hover
    this._mouseEnterHandler = (e) => {
      const el = e.target.closest('[data-sfx-hover]');
      if (el) {
        const sfxKey = el.dataset.sfxHover;
        const howl = Storage.getPreloadedAudio('sounds', sfxKey);
        const status = howl ? 'loaded' : 'not loaded';
        console.debug(`[AudioManager] SFX hover: "${sfxKey}" (${status})`);
        this.playSfx(sfxKey);
      }
    };

    // Use capture to ensure we get events before they might be stopped
    document.addEventListener('click', this._clickHandler, true);
    document.addEventListener('mouseenter', this._mouseEnterHandler, true);

    this._sfxDelegationInitialized = true;
    console.debug('[AudioManager] SFX event delegation initialized');
  }

  /**
   * Limpia los event listeners de SFX
   * @private
   */
  _destroySfxEventDelegation() {
    if (this._clickHandler) {
      document.removeEventListener('click', this._clickHandler, true);
      this._clickHandler = null;
    }
    if (this._mouseEnterHandler) {
      document.removeEventListener('mouseenter', this._mouseEnterHandler, true);
      this._mouseEnterHandler = null;
    }
    this._sfxDelegationInitialized = false;
  }

  /**
   * Limpia recursos (para cleanup)
   */
  destroy() {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }

    if (this.currentMusic) {
      this.currentMusic.unload();
      this.currentMusic = null;
    }

    this._destroySfxEventDelegation();

    this.currentMusicKey = null;
    this.isUnlocked = false;
    this.pendingPlay = null;
  }
}

// Singleton
const AudioManager = new AudioManagerClass();

export default AudioManager;
